import React, { useEffect, useMemo, useRef, useState } from "react";
import { Pagination, Schema, Form, InputNumber } from "rsuite";
import { useIntl } from "react-intl";
import styled from "styled-components";

import { DEFAULT_PER_PAGE_MEDIUM } from "const";
import { debounce } from "utils";

import CustomField from "../../Form/CustomField";

import { useWindowWidth } from "hooks";
import m from "definedMessages";
import s from "./Pagination.module.css";

const { NumberType } = Schema.Types;


export default ({
    total = 0,
    perPage = DEFAULT_PER_PAGE_MEDIUM,
    perPageMaximum = 10000,
    limitWidth = 475,
    activePage = 1,
    hidePages,
    hidePerPage,
    onChangePage,
    onChangePerPage,
    showedCount,
    showFirstAndLast = true,
    disabled = false,
    noWrapper = false
}) => {
    const [formValue, setFormValue] = useState({ per_page: perPage });
    const lastEmittedPerPageRef = useRef(+perPage || null);

    useEffect(() => {
        setFormValue({ per_page: perPage });
        lastEmittedPerPageRef.current = +perPage || null;
    }, [perPage]);

    const handleChange = ({ per_page: rawPerPage = 0 }) => {
        const nextPerPage = +rawPerPage;

        if (!nextPerPage) {
            return null;
        }

        if (nextPerPage === +perPage || nextPerPage === lastEmittedPerPageRef.current) {
            return null;
        }

        lastEmittedPerPageRef.current = nextPerPage;
        return onChangePerPage(nextPerPage);
    };

    const perPageModel = Schema.Model({
        per_page: NumberType()
            .isInteger("Integer")
            .isRequired("Required")
            .min(1, "Minimum 1")
            .max(perPageMaximum, `Maximum ${perPageMaximum}`),
    });

    const onPerPageChange = useMemo(() => debounce((value) => {
        const formChecked = perPageModel.check(value);
        const formValid = formChecked?.per_page && !formChecked.per_page?.hasError;

        if (formValid)
            handleChange(value);
    }, 500), [onChangePerPage, perPageMaximum, perPage]);

    const { formatMessage } = useIntl();
    const resizedWidth = useWindowWidth();

    return (
        <div className={noWrapper ? s.noWrapper : s.wrapper}>
            {!hidePages && <div className={s.pagination}>
                <StyledPagination
                    size="xs"
                    pages={Math.ceil(total / perPage)}
                    activePage={activePage || 1}
                    total={total || 0}
                    maxButtons={5}
                    onSelect={onChangePage}
                    // disabled={disabled || total === 0}
                    first={showFirstAndLast}
                    last={showFirstAndLast}
                    next
                    prev
                />
            </div>}

            {!hidePerPage &&
                <Form
                    layout="inline"
                    model={perPageModel}
                    className={s.form}
                    formValue={formValue}
                    onChange={(value) => {
                        setFormValue(value);
                        onPerPageChange(value);
                    }}
                >
                    <CustomField
                        name="per_page"
                        accepter={InputNumber}
                        errorPlacement="topRight"
                        max={perPageMaximum}
                        min={1}
                        size="sm"
                        style={{ width: 135 }}
                        placeholder={formatMessage(m.rowsPerPage)}
                        disabled={disabled}
                    />
                </Form>
            }

            {!!showedCount &&
                <StyledCount>
                    {(resizedWidth > (limitWidth + 75)) && formatMessage(m.total) + ":"} {showedCount}
                </StyledCount>}

        </div>
    );
};

const StyledCount = styled.div`
    && {
        margin: 5px 6px;
        color: gray
    }
`;

const StyledPagination = styled(Pagination)`
    && {
        border: 1px solid #C4C4C4;
        border-radius: 3px;
        
        a {
            color: #C4C4C4;
            border-right: 1px solid #C4C4C4;
            border-radius: 0 !important;
        }
        
        .rs-pagination-btn:last-child a {
            border-right: none;
        }
        
        & > li.rs-pagination-btn-active > a {
            color: #fff;
            background-color: var(--color-brand2);
        }
    }
`;